import ApiClient from './ApiClient';

class IamClient extends ApiClient {
  constructor(url) {
    super(url ? `${url}` : '');
  }

  //#TODO: temp hardcoded value 'demo'
  async getCustomers() {
    try {
      return await this.get(`/tenant`);
    } catch (error) {
      throw new Error(`Failed to get users info: ${error.message}`);
    }
  }

  // One page of the organisation's fleet, pre-joined for the All Devices table
  // (customer, contract, pool, location, latest calc). GET tenant no longer embeds devices.
  // params: { page, pageSize, search, userId, locationId, contractId, poolId, status,
  //           deviceClass, sort, desc }
  async getTenantDevices(params = {}) {
    try {
      return await this.get(`/tenant/devices`, { params });
    } catch (error) {
      throw new Error(`Failed to get devices page: ${error.message}`);
    }
  }

  // One page of customers with device counts + yesterday's billing figures, and org-wide
  // totals for the headline cards. params: { page, pageSize, search, sort, desc }
  async getTenantCustomers(params = {}) {
    try {
      return await this.get(`/tenant/customers`, { params });
    } catch (error) {
      throw new Error(`Failed to get customers page: ${error.message}`);
    }
  }

  async getWatcherLinks() {
    try {
      return await this.get(`/pool-watcher-links/organization`);
    } catch (error) {
      throw new Error(`Failed to get users info: ${error.message}`);
    }
  }

  async updateUserWatcherLinks(id, formData) {
    try {
      return await this.post(`pool-watcher-links/user/${id}`, formData);
    } catch (error) {
      throw new Error(`Failed to get users info: ${error.message}`);
    }
  }

  async getCustomer() {
    try {
      return await this.get(`/user`);
    } catch (error) {
      throw new Error(`Failed to get user info: ${error.message}`);
    }
  }

  async getTransactions() {
    try {
      return await this.get(`/tenant/electricitycosttransaction`);
    } catch (error) {
      throw new Error(`Failed to get users transactions: ${error.message}`);
    }
  }

  async getSpecificTransactions() {
    try {
      return await this.get(`/tenant/fiattransaction`);
    } catch (error) {
      throw new Error(
        `Failed to get user specific transactions: ${error.message}`
      );
    }
  }

  async fetchTenants() {
    try {
      return await this.get(`/tenants`);
    } catch (error) {
      throw new Error(`Failed to fetch tenants: ${error.message}`);
    }
  }

  // POST users — creates the Auth0 login (added to this org's Auth0 Organization) and the
  // billing User row in one call. Returns the created customer shaped like a GET tenant
  // users[] entry so the customers slice can append it directly.
  async createCustomer(formData) {
    try {
      const data = await this.post(`/users`, formData);
      if (!data?.success) {
        throw new Error(data?.errorMessage || 'Customer creation failed');
      }
      return data.customer;
    } catch (error) {
      const apiMessage = error?.response?.data?.errorMessage;
      throw new Error(apiMessage || error.message);
    }
  }

  async updateCustomer(formData) {
    try {
      return await this.put(`/user/update`, formData);
    } catch (error) {
      throw new Error(`Failed to update customer: ${error.message}`);
    }
  }

  async updateCustomerSellingElectricityPrice(id, formData) {
    try {
      return await this.put(`/users/${id}/electricity-selling-price`, formData);
    } catch (error) {
      throw new Error(
        `Failed to update customer electricity selling price: ${error.message}`
      );
    }
  }

  async updateCustomerNote(id, formData) {
    try {
      return await this.put(`/users/${id}/note`, formData);
    } catch (error) {
      throw new Error(`Failed to update customer note: ${error.message}`);
    }
  }

  async updateCustomerBalance(id, formData) {
    try {
      return await this.post(`/users/${id}/balance/adjust`, formData);
    } catch (error) {
      throw new Error(`Failed to update customer balance: ${error.message}`);
    }
  }

  async updateCustomerDeposit(id, formData) {
    try {
      return await this.put(`/users/${id}/deposit`, formData);
    } catch (error) {
      throw new Error(`Failed to update customer deposit: ${error.message}`);
    }
  }

  async getAllPaymentRequests() {
    try {
      return await this.get(`/xadminmanualpayments`);
    } catch (error) {
      throw new Error(`Failed to get payment requests: ${error.message}`);
    }
  }

  async createPaymentRequest(formData) {
    try {
      return await this.post(`/manual/create`, formData);
    } catch (error) {
      throw new Error(`Failed to create payment request: ${error.message}`);
    }
  }

  async updateCustomerCompanyId(id, formData) {
    try {
      return await this.put(`/users/${id}/company-id`, formData);
    } catch (error) {
      throw new Error(`Failed to update company id: ${error.message}`);
    }
  }

  async updateCustomerVatPercentage(id, formData) {
    try {
      return await this.put(`/users/${id}/vat`, formData);
    } catch (error) {
      throw new Error(
        `Failed to update customer vat percentage: ${error.message}`
      );
    }
  }

  async updateCustomerVatNumber(id, formData) {
    try {
      return await this.put(`/users/${id}/vat-number`, formData);
    } catch (error) {
      throw new Error(`Failed to update customer vat number: ${error.message}`);
    }
  }

  async onboardTenant(formData) {
    try {
      return await this.post(`/tenants`, formData);
    } catch (error) {
      throw new Error(`Failed to onboard tenant: ${error.message}`);
    }
  }

  async activateTenant(tenantId) {
    try {
      return await this.post(`/tenants/${tenantId}/activate`);
    } catch (error) {
      throw new Error(`Failed to activate tenant: ${error.message}`);
    }
  }

  async deactivateTenant(tenantId) {
    try {
      return await this.post(`/tenants/${tenantId}/deactivate`);
    } catch (error) {
      throw new Error(`Failed to deactivate tenant: ${error.message}`);
    }
  }

  async getSpecificTenant(tenantKey) {
    try {
      return await this.get(`/tenants/${tenantKey}`);
    } catch (error) {
      throw new Error(`Failed to get tenant data: ${error.message}`);
    }
  }

  async deleteUser(id) {
    try {
      return await this.delete(`/users/${id}`);
    } catch (error) {
      throw new Error(`Failed to delete user: ${error.message}`);
    }
  }
}

export default IamClient;
